import React, { useEffect } from 'react';
import { usePricing } from '../store/pricing';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Slider } from './ui/slider';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Loader2, Check, Zap, Shield, Users, HardDrive, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

export const PricingConfigurator = () => {
  const {
    storage_gb,
    collabora_users,
    antivirus,
    hp_backend,
    prices,
    breakdown,
    loading,
    error,
    setStorageGb,
    setCollaboraUsers,
    setAntivirus,
    setHpBackend,
    fetchPrices,
    calculatePrice
  } = usePricing();

  // Fetch prices on mount
  useEffect(() => {
    fetchPrices();
  }, [fetchPrices]);

  useEffect(() => {
    calculatePrice().catch(() => {
      toast.error('Preisberechnung fehlgeschlagen. Bitte versuche es erneut.');
    });
  }, [storage_gb, collabora_users, antivirus, hp_backend, calculatePrice]);

  return (
    <div className="grid gap-8 lg:grid-cols-3" data-testid="pricing-configurator">
      {/* Left: Configurator */}
      <div className="lg:col-span-2">
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="font-display text-2xl flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-primary" />
              Bau dir deine Helden-Wolke
            </CardTitle>
            <CardDescription>
              Wähle deine Superkräfte und Add-ons. Du siehst den Preis live.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-8">
            {/* Storage */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="storage-slider" className="text-base font-medium flex items-center gap-2">
                  <HardDrive className="h-4 w-4 text-primary" />
                  Helden-Speicher
                </Label>
                <Badge variant="secondary" className="bg-primary/10 text-primary" data-testid="storage-value">
                  {storage_gb} GB
                </Badge>
              </div>
              <Slider
                id="storage-slider"
                min={250}
                max={2000}
                step={50}
                value={[storage_gb]}
                onValueChange={([value]) => setStorageGb(value)}
                data-testid="storage-slider"
              />
              <p className="text-sm text-muted-foreground">
                🦸 Heldenhafte 250 GB sind inklusive. Jede weiteren 50 GB kosten {prices?.storage_extra_50gb ? `${prices.storage_extra_50gb.toFixed(2).replace('.', ',')} €/Monat` : '...'}.
              </p>
            </div>

            {/* Collabora Users */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="collabora-slider" className="text-base font-medium flex items-center gap-2">
                  <Users className="h-4 w-4 text-primary" />
                  Team-Power (Collabora Office)
                </Label>
                <Badge variant="secondary" className="bg-primary/10 text-primary" data-testid="collabora-value">
                  {collabora_users} Helden
                </Badge>
              </div>
              <Slider
                id="collabora-slider"
                min={0}
                max={50}
                step={1}
                value={[collabora_users]}
                onValueChange={([value]) => setCollaboraUsers(value)}
                data-testid="collabora-slider"
              />
              <p className="text-sm text-muted-foreground">
                🤝 Gemeinsam heldenhaft Dokumente bearbeiten. {prices?.collabora_per_user ? `${prices.collabora_per_user.toFixed(2).replace('.', ',')} €/Monat pro Held` : '...'}.
              </p>
            </div>

            {/* High Performance Backend */}
            <div className="flex items-center justify-between rounded-lg border border-primary/20 p-4 bg-gradient-to-r from-primary/5 to-transparent">
              <div className="space-y-0.5">
                <Label htmlFor="hp-backend" className="text-base font-medium flex items-center gap-2">
                  <Zap className="h-4 w-4 text-amber-500" />
                  Turbo-Antrieb aktivieren
                </Label>
                <p className="text-sm text-muted-foreground">
                  ⚡ Für Nextcloud Talk & Video-Konferenzen. {prices?.hp_backend ? `${prices.hp_backend.toFixed(2).replace('.', ',')} €/Monat` : '...'}
                </p>
              </div>
              <Switch
                id="hp-backend"
                checked={hp_backend}
                onCheckedChange={setHpBackend}
                data-testid="hp-backend-switch"
              />
            </div>

            {/* Antivirus */}
            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <Label htmlFor="antivirus" className="text-base font-medium flex items-center gap-2">
                  <Shield className="h-4 w-4 text-green-500" />
                  Helden-Schutzschild (Antivirus)
                </Label>
                <p className="text-sm text-muted-foreground">
                  🛡️ Extra Scan von Uploads. {prices?.antivirus ? `${prices.antivirus.toFixed(2).replace('.', ',')} €/Monat` : '...'}
                </p>
              </div>
              <Switch
                id="antivirus"
                checked={antivirus}
                onCheckedChange={setAntivirus}
                data-testid="antivirus-switch"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Right: Price Breakdown (Sticky) */}
      <div className="lg:col-span-1">
        <div className="sticky top-20">
          <Card className="glass-card border-primary/20 overflow-hidden">
            <div className="bg-gradient-to-r from-primary to-blue-600 px-6 py-4">
              <CardTitle className="font-display text-xl text-white">Dein Helden-Tarif</CardTitle>
              <CardDescription className="text-white/80">pro Monat, inkl. MwSt.</CardDescription>
            </div>
            <CardContent className="space-y-4 pt-6">
              {loading && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              )}

              {error && (
                <div className="rounded-lg bg-destructive/10 p-4 text-sm text-destructive">
                  {error}
                </div>
              )}

              {breakdown && !loading && (
                <>
                  {/* Breakdown Items */}
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground flex items-center gap-1">
                        🦸 Helden-Basis (250 GB)
                      </span>
                      <span className="font-medium">{breakdown.base_package.toFixed(2)} €</span>
                    </div>

                    {breakdown.extra_storage > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">
                          💪 Extra-Kraft ({breakdown.extra_storage_gb} GB)
                        </span>
                        <span className="font-medium">{breakdown.extra_storage.toFixed(2)} €</span>
                      </div>
                    )}

                    {breakdown.collabora > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">
                          🤝 Team-Power ({breakdown.collabora_users} Helden)
                        </span>
                        <span className="font-medium">{breakdown.collabora.toFixed(2)} €</span>
                      </div>
                    )}

                    {breakdown.antivirus > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">🛡️ Schutzschild</span>
                        <span className="font-medium">{breakdown.antivirus.toFixed(2)} €</span>
                      </div>
                    )}

                    {breakdown.hp_backend > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">⚡ Turbo-Antrieb</span>
                        <span className="font-medium">{breakdown.hp_backend.toFixed(2)} €</span>
                      </div>
                    )}
                  </div>

                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-semibold">Gesamt</span>
                      <span
                        className="text-3xl font-bold text-primary font-display"
                        data-testid="live-price-amount"
                      >
                        {breakdown.total.toFixed(2)} €
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      pro Monat, inkl. MwSt.
                    </p>
                  </div>

                  {/* Features included */}
                  <div className="space-y-2 rounded-lg bg-gradient-to-r from-primary/10 to-transparent p-4">
                    <p className="text-sm font-medium flex items-center gap-1">
                      <Sparkles className="h-4 w-4 text-primary" />
                      Immer inklusive:
                    </p>
                    <ul className="space-y-1 text-xs text-muted-foreground">
                      {[
                        '🚀 Hochverfügbarkeit (selbstheilend)',
                        '☁️ S3-Objektspeicher',
                        '🔄 Automatische Updates',
                        '🦸 Helden-Support',
                      ].map((item, i) => (
                        <li key={i} className="flex items-center gap-2">
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
